import React from 'react';

import DynamicStateService, {
  DynamicStateScopes,
} from '~features/DynamicStateService';

type RenderProofSeenState = {
  viewed?: boolean;
  dismissed?: boolean;
};

type DynamicStateCompanyWithRenderProof = {
  RenderProofEntryPoint?: RenderProofSeenState;
};

export type RenderProofSeen = {
  /** False until the account's stored state has actually been read. */
  isKnown: boolean;
  /** The account has already been shown the proof, on any surface. */
  viewed: boolean;
  dismissed: boolean;
  markViewed: () => void;
  dismiss: () => void;
};

/**
 * Whether this **account** has already been shown its render proof.
 *
 * Account-scoped, not login-scoped, and shared by both surfaces:
 *
 * - the experiment buckets on `companyId` and the proof is for the account's
 *   domain, so two colleagues on one account are one exposure, not two. Under
 *   login scope they each got their own banner and each dismissed it
 *   separately, which double-counts the conversion they jointly produced;
 * - the wizard writes it too. The dashboard entry point exists to catch
 *   accounts the wizard could not serve in time, so an account that already
 *   saw the proof at the end of integration must not be offered it again for
 *   the next two weeks.
 */
export function useRenderProofSeen(): RenderProofSeen {
  const [currentDynamicState, updateDynamicState] =
    DynamicStateService.useDynamicStateService<
      Record<string, never>,
      DynamicStateCompanyWithRenderProof
    >();

  const seen = currentDynamicState?.company?.RenderProofEntryPoint;

  // Only the changed field is sent. Both layers merge rather than replace —
  // `DynamicStateService` does `Object.assign` on the namespace in the query
  // cache, and the upsert in `ui-state.service.ts` shallow-merges by top-level
  // namespace, preserving the ones absent from the update. Spreading a
  // render-time snapshot in here would add nothing except a stale value to
  // write back and a reason for these callbacks to change identity.
  const patch = React.useCallback(
    (change: RenderProofSeenState) => {
      updateDynamicState({
        scope: DynamicStateScopes.Company,
        state: { RenderProofEntryPoint: change },
      });
    },
    [updateDynamicState],
  );

  const markViewed = React.useCallback(() => patch({ viewed: true }), [patch]);
  const dismiss = React.useCallback(() => patch({ dismissed: true }), [patch]);

  return {
    isKnown: currentDynamicState !== undefined,
    viewed: seen?.viewed ?? false,
    dismissed: seen?.dismissed ?? false,
    markViewed,
    dismiss,
  };
}
